import { Capacitor, registerPlugin } from '@capacitor/core';
import { apiRequest } from '../api/client';

export interface AcquisitionStatus {
  enabled: boolean; eligible: boolean; prompt: boolean; allowed: boolean; text_version: string;
}
interface AttributionPayload { token?: string; referrer?: string; click_at?: number; install_begin_at?: number }
interface AttributionPlugin { collect(options: { consent: true }): Promise<AttributionPayload> }
export function attributionPlatform(): 'ios' | 'android' | null {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('Attribution')) return null;
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android' ? platform : null;
}
export const acquisitionStatus = () => apiRequest<AcquisitionStatus>('/api/acquisition/status');
const post = <T>(path: string, id: number, body: Record<string, unknown> = {}) =>
  apiRequest<T>(`/api/acquisition/${path}`, { method: 'POST', timeoutMs: path === 'collect' ? 45000 : undefined, body: { ...body, expected_user_id: id } });
export const claimAttributionPrompt = (id: number) => post<{ claimed: boolean; text_version: string }>('prompt', id);
export const withdrawAttribution = (id: number) => post<AcquisitionStatus>('withdraw', id);

export async function answerAttribution(id: number, choice: 'allow' | 'deny', version: string, stillCurrent: () => boolean) {
  const platform = attributionPlatform();
  if (!platform || !stillCurrent()) return;
  await post<AcquisitionStatus>('consent', id, { choice, platform, text_version: version });
  if (choice !== 'allow' || !stillCurrent()) return;
  // Kontoübergreifende Einmal-Sperre VOR dem nativen Zugriff, auch über Geräte hinweg.
  const claim = await post<{ attempt_id: string; platform: string }>('collection/claim', id);
  if (!stillCurrent() || claim.platform !== platform) return;
  const payload = await registerPlugin<AttributionPlugin>('Attribution').collect({ consent: true });
  if (!stillCurrent()) return;
  // Kein Gerätecache. Die Abrufkennung ist zusätzlich serverseitig ans Konto gebunden.
  await post('collect', id, { ...payload, attempt_id: claim.attempt_id });
}

export const isAttributionOverview = (path: string) => ['/', '/dashboard', '/chats', '/characters', '/discover', '/world', '/table-rounds'].includes(path);

export function quietAttributionMoment(path: string, gate: string, started: number, now: number) {
  // Erst auf einer Übersichtsseite, nach zwei Minuten. Niemals im Chat/Kauf/Moderation.
  if (!isAttributionOverview(path)) return false;
  if (gate !== 'none' || now - started < 120000 || document.visibilityState === 'hidden') return false;
  if (document.querySelector('dialog[open], [role="dialog"], [aria-modal="true"], [data-lorath-dock], [data-tour-root], [aria-busy="true"]')) return false;
  const active = document.activeElement as HTMLElement | null;
  return !(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active?.isContentEditable);
}
